import React, { Component, Fragment } from "react";
import DispatchProps from "../../../utils/DispatchProps";
import Actions from "../../../redux/Actions";
import OnboardHelpBox from "./OnboardHelpBox";
import OnboardTimeline, {OnboardTimelineItem} from "./OnboardTimeline";
import OnboardNavigation from "./OnboardNavigation";
import { IRootState } from "../../../store";
import { connect } from "react-redux";
import { History } from "history";
import {
    Department,
    SchoolWorkloadConfig,
} from "../../../models/SchoolWorkloadConfig";
import ApiService from "../../ApiService";
import * as _ from "lodash";
import SelectableCard from "./SelectableCard";
import { SubjectItem } from "./OnboardSubjectMergePage/SubjectItem";
import SelectableSubjectCard from "./SelectableSubjectCard";
import { MessageBarType, PrimaryButton } from "office-ui-fabric-react";
import SubjectMergeEditor from "../../SubjectMergeEditor";
import School from "../../../models/School";

interface OnboardDepartmentSubjectsPageProps {
    tenantId: string;
    schoolCode: string;

    apiService: ApiService;
    history: History;
}

interface OnboardDepartmentSubjectsPageState {
    schoolWorkloadConfig?: SchoolWorkloadConfig;
    departments: Department[];
    selectedDepartment?: Department;
    allSubjectItems: SubjectItem[];
    selectedSubjectCodes: string[];
}

class OnboardDepartmentSubjectsPage extends Component<OnboardDepartmentSubjectsPageProps & DispatchProps, OnboardDepartmentSubjectsPageState> {

    constructor(props: OnboardDepartmentSubjectsPageProps & DispatchProps) {
        super(props);
        this.state = {
            schoolWorkloadConfig: undefined,
            departments: [],
            selectedDepartment: undefined,
            allSubjectItems: [],
            selectedSubjectCodes: []
        };
    }

    async componentDidMount(): Promise<void> {
        if (this.props.tenantId)
        {

            const schoolWorkloadConfig = await this.props.apiService.getSchoolConfigJson(this.props.tenantId, this.props.schoolCode);
            const subjectMerges = await this.props.apiService.getSubjectMergesForSchool(this.props.tenantId,this.props.schoolCode);
            const sdsSubjects = await this.props.apiService.getSdsSubjectsForSchool(this.props.tenantId, this.props.schoolCode);
            const subjectCodeSdsSubjectDict = _.keyBy(sdsSubjects, "subjectCode");
            const allSubjectItems = SubjectMergeEditor.calcSubjectItemsFromConfigAndSdsSubjects(subjectMerges, schoolWorkloadConfig.subjectExcludes, subjectCodeSdsSubjectDict);

            this.setState({
                schoolWorkloadConfig,
                allSubjectItems
            });
        }
    }

    async componentDidUpdate(prevProps: Readonly<OnboardDepartmentSubjectsPageProps & DispatchProps>, prevState: OnboardDepartmentSubjectsPageState): Promise<void> {
        if (this.state.schoolWorkloadConfig !== prevState.schoolWorkloadConfig) {
            const departments = _.cloneDeep(this.state.schoolWorkloadConfig?.departments?.items || []);
            this.setState({
                departments: _.sortBy(departments, ["rawName"])
            });
        }
    }

    private save = async (): Promise<boolean> => {
        if (this.state.schoolWorkloadConfig === undefined) {
            return false;
        }

        const updatedConfig = _.cloneDeep(this.state.schoolWorkloadConfig);

        updatedConfig.departments.items = this.state.departments;

        const savedSchoolWorkloadConfig = await this.props.apiService.putSchoolConfigJson(this.props.tenantId, this.props.schoolCode, updatedConfig);
        this.setState({
            schoolWorkloadConfig: savedSchoolWorkloadConfig
        });

        await this.props.dispatch(Actions.showMessageBar("Saved department grouping to config", MessageBarType.success));

        return true;
    }

    private toggleSubjectSelection = (subjectCode: string): void => {
        if (_.includes(this.state.selectedSubjectCodes, subjectCode)) {
            this.setState({
                selectedSubjectCodes: _.without(this.state.selectedSubjectCodes, subjectCode)
            });
        }
        else {
            this.setState({
                selectedSubjectCodes: [...this.state.selectedSubjectCodes, subjectCode]
            });
        }
    };

    private addSelectedSubjectsToSelectedDepartment = (): void => {
        if (this.state.selectedDepartment === undefined) {
            return;
        }

        const codesToAdd = this.state.selectedSubjectCodes.filter(code => {
            return !_.includes(this.state.selectedDepartment!.sourceSubjectCodes, code);
        });

        this.state.selectedDepartment.sourceSubjectCodes = [...this.state.selectedDepartment.sourceSubjectCodes, ...codesToAdd];

        this.setState({
            selectedSubjectCodes: []
        });
    }

    private removeSubjectFromDepartment = (department: Department, subjectCode: string): void => {
        department.sourceSubjectCodes = _.without(department.sourceSubjectCodes, subjectCode);
    }

    private getDepartmentName(department: Department | undefined): string {
        return department !== undefined ? department.rawName : "";
    }

    private getSubjectByCode(code: string): SubjectItem | undefined{
        return this.state.allSubjectItems.find(s => s.code === code);
    }

    public render(): JSX.Element {

        const selectPrompt = (<span>Select a department and the subjects that belong in it</span>);
        const addToGroupingButton = (<PrimaryButton onClick={this.addSelectedSubjectsToSelectedDepartment} text={`Add ${this.state.selectedSubjectCodes.length} subject(s) to ${this.getDepartmentName(this.state.selectedDepartment)}`} />);

        const departmentCards = this.state.departments.map((department) => {
            const subjectNames = department.sourceSubjectCodes.map(subjectCode => <li key={subjectCode}>{this.getSubjectByCode(subjectCode)?.name} - {subjectCode}  <a onClick={() => this.removeSubjectFromDepartment(department, subjectCode)}>(remove)</a></li>);
            return (
                <SelectableCard
                    key={department.rawName}
                    className={"flex-column"}
                    title={department.rawName}
                    selected={this.state.selectedDepartment === department}
                    onClick={() => {
                        this.setState({
                            selectedDepartment: department
                        });
                    }}>
                    <ul>
                        {subjectNames}
                    </ul>
                </SelectableCard>
            );
        });

        const subjectCards = this.state.allSubjectItems.sort(SubjectItem.AlphabeticalCompare).map((subjectItem) => {
            var selected = _.includes(this.state.selectedSubjectCodes, subjectItem.code);
            return (
                <SelectableSubjectCard
                    key={subjectItem.code}
                    subjectItem={subjectItem}
                    selected={selected}
                    iconName={selected ? "CheckboxComposite" : "Checkbox"}
                    iconLabel={"Select"}
                    toggleSelected={() => this.toggleSubjectSelection(subjectItem.code)}/>
            );
        });

        return (
            <Fragment>
                <OnboardHelpBox currentTimelineItemKey={"department-subjects"}/>
                <OnboardTimeline tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} apiService={this.props.apiService}>
                    {(school: School,timelineItems: OnboardTimelineItem[]) => {
                        return (
                            <Fragment>
                                <div className={"flex-column"}>
                                    <div className={"flex-row flex--justify-content-center"}>
                                        {this.state.selectedSubjectCodes.length > 0 && this.state.selectedDepartment !== undefined ? addToGroupingButton : selectPrompt}
                                    </div>
                                    <div className="flex-row flex-item--flex-1 flex">
                                        <div className={"flex-item--flex-1"}>
                                            <h3>Departments</h3>
                                            {departmentCards}
                                        </div>
                                        <div className={"flex-item--flex-3"}>
                                            <h3>Subjects</h3>
                                            <div className={"flex-row flex--flow-wrap"}>
                                                {subjectCards}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <OnboardNavigation currentTimelineItemKey={"department-subjects"} timelineItems={timelineItems}
                                                   completeItem={this.save} enableNext={true} enableSave={true}
                                                   history={this.props.history} tenantId={this.props.tenantId}
                                                   schoolCode={this.props.schoolCode}/>
                            </Fragment>
                        );
                    }}
                </OnboardTimeline>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: any): OnboardDepartmentSubjectsPageProps => {

        const tenantId = ownProps.match.params.tenantId;
        const schoolCode = ownProps.match.params.schoolCode;

        return {
            tenantId,
            schoolCode,
            apiService: state.auth.apiService,
            history: ownProps.history
        };
    }
)(OnboardDepartmentSubjectsPage)